/**
 * ProcessEditorKanban Component
 *
 * Kanban-style process editor with phases as columns.
 * Full CRUD for phases, events, and tasks.
 *
 * US-PE-002b: Process Management UI Layer
 */

import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { PageLayout } from '@/components/layout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import {
  useProcess,
  usePhaseMutations,
  useEventMutations,
  useTaskMutations
} from '@/hooks/useProcesses'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { toast } from 'sonner'
import {
  ArrowLeft,
  Plus,
  MoreHorizontal,
  Play,
  Square,
  Diamond,
  Clock,
  Circle,
  Pencil,
  Trash2,
  Loader2,
  GripVertical,
  Package
} from 'lucide-react'
import RecipeTab from './RecipeTab'

/**
 * Event type icon
 */
function EventIcon({ type, className }) {
  const icons = {
    start: <Play className={cn("h-3 w-3 text-emerald-500", className)} />,
    end: <Square className={cn("h-3 w-3 text-gray-400", className)} />,
    milestone: <Diamond className={cn("h-3 w-3 text-blue-500", className)} />
  }
  return icons[type] || icons.milestone
}

/**
 * Task item within an event card
 */
function TaskItem({ task, onEdit, onDelete }) {
  const [isHovered, setIsHovered] = useState(false)

  return (
    <div
      className={cn(
        "flex items-center gap-2 py-1 px-2 -mx-2 rounded text-xs",
        "hover:bg-muted/50 transition-colors group"
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Circle className="h-3 w-3 text-muted-foreground/50 flex-shrink-0" />
      <span className="flex-1 text-muted-foreground truncate">{task.name}</span>
      {task.estimated_minutes && (
        <span className="text-[10px] text-muted-foreground/60 tabular-nums">
          {task.estimated_minutes}m
        </span>
      )}
      {isHovered && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="p-0.5 rounded hover:bg-muted">
              <MoreHorizontal className="h-3 w-3 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-32 bg-white shadow-lg">
            <DropdownMenuItem onClick={() => onEdit(task)}>
              <Pencil className="h-3 w-3 mr-2" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => onDelete(task)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="h-3 w-3 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )
}

/**
 * Event card within a phase column
 */
function EventCard({ event, onEdit, onDelete, onAddTask, onEditTask, onDeleteTask }) {
  const [isHovered, setIsHovered] = useState(false)
  const tasks = (event.event_tasks || []).sort((a, b) => a.sequence_order - b.sequence_order)

  return (
    <div
      className={cn(
        "bg-card border rounded-lg p-3 shadow-sm",
        "hover:shadow-md hover:border-border/80 transition-all",
        "cursor-default"
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Event header */}
      <div className="flex items-start gap-2 mb-1">
        <EventIcon type={event.event_type} className="mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm truncate">{event.name}</div>
          <div className="text-xs text-muted-foreground">Day {event.offset_days}</div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className={cn(
                "p-1 rounded hover:bg-muted transition-opacity -mr-1 -mt-1",
                isHovered ? "opacity-100" : "opacity-0"
              )}
            >
              <MoreHorizontal className="h-3 w-3 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-36 bg-white shadow-lg">
            <DropdownMenuItem onClick={() => onEdit(event)}>
              <Pencil className="h-3 w-3 mr-2" />
              Edit event
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onAddTask(event)}>
              <Plus className="h-3 w-3 mr-2" />
              Add task
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => onDelete(event)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="h-3 w-3 mr-2" />
              Delete event
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Tasks */}
      {tasks.length > 0 && (
        <div className="border-t pt-2 mt-2 space-y-0.5">
          {tasks.map(task => (
            <TaskItem
              key={task.id}
              task={task}
              onEdit={onEditTask}
              onDelete={onDeleteTask}
            />
          ))}
        </div>
      )}

      {/* Add task button (compact) */}
      <button
        onClick={() => onAddTask(event)}
        className="w-full mt-2 py-1 text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center gap-1"
      >
        <Plus className="h-3 w-3" />
        Add task
      </button>
    </div>
  )
}

/**
 * Sortable Phase column
 */
function PhaseColumn({
  phase,
  index,
  onEditPhase,
  onDeletePhase,
  onAddEvent,
  onEditEvent,
  onDeleteEvent,
  onAddTask,
  onEditTask,
  onDeleteTask,
  onManageRecipe
}) {
  const [isHovered, setIsHovered] = useState(false)
  const events = (phase.phase_events || []).sort((a, b) => a.sequence_order - b.sequence_order)
  const taskCount = events.reduce((sum, e) => sum + (e.event_tasks?.length || 0), 0)

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: phase.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex flex-col w-72 flex-shrink-0",
        isDragging && "opacity-50 z-50"
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Column header */}
      <div className="bg-muted/50 rounded-t-lg p-3 border border-b-0">
        <div className="flex items-center gap-2 mb-1">
          {/* Drag handle */}
          <button
            {...attributes}
            {...listeners}
            className={cn(
              "p-0.5 rounded cursor-grab active:cursor-grabbing hover:bg-muted transition-opacity",
              isHovered ? "opacity-100" : "opacity-0"
            )}
          >
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </button>
          <span className="w-5 h-5 rounded bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">
            {index + 1}
          </span>
          <h3 className="font-medium text-sm flex-1 truncate">{phase.name}</h3>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={cn(
                  "p-1 rounded hover:bg-muted transition-opacity",
                  isHovered ? "opacity-100" : "opacity-0"
                )}
              >
                <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40 bg-white shadow-lg">
              <DropdownMenuItem onClick={() => onEditPhase(phase)}>
                <Pencil className="h-3 w-3 mr-2" />
                Edit phase
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onManageRecipe(phase)}>
                <Package className="h-3 w-3 mr-2" />
                Manage recipe
                {phase.recipe_template_id && (
                  <Badge variant="secondary" className="ml-auto text-[10px] h-4 px-1">Set</Badge>
                )}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onAddEvent(phase)}>
                <Plus className="h-3 w-3 mr-2" />
                Add event
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => onDeletePhase(phase)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-3 w-3 mr-2" />
                Delete phase
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {phase.typical_duration_days}d
          </span>
          <span>{events.length} events</span>
          <span>{taskCount} tasks</span>
          {phase.recipe_template_id && (
            <span className="flex items-center gap-1 text-amber-600">
              <Package className="h-3 w-3" />
              Recipe
            </span>
          )}
        </div>
      </div>

      {/* Column body with event cards */}
      <div className="flex-1 bg-muted/20 border border-t-0 rounded-b-lg p-2 space-y-2 min-h-[200px] overflow-y-auto">
        {events.map(event => (
          <EventCard
            key={event.id}
            event={event}
            onEdit={onEditEvent}
            onDelete={onDeleteEvent}
            onAddTask={onAddTask}
            onEditTask={onEditTask}
            onDeleteTask={onDeleteTask}
          />
        ))}

        {/* Add event button */}
        <button
          onClick={() => onAddEvent(phase)}
          className="w-full py-2 px-3 rounded-lg border-2 border-dashed border-muted-foreground/20 text-sm text-muted-foreground hover:border-muted-foreground/40 hover:text-foreground transition-colors flex items-center justify-center gap-2"
        >
          <Plus className="h-3 w-3" />
          Add event
        </button>
      </div>
    </div>
  )
}

/**
 * Phase Dialog (Add/Edit)
 */
function PhaseDialog({ open, onOpenChange, phase, processId, onSave }) {
  const [formData, setFormData] = useState({
    name: '',
    typical_duration_days: 1,
    description: ''
  })
  const [saving, setSaving] = useState(false)
  const { createPhase, updatePhase } = usePhaseMutations()

  const isEdit = !!phase

  useEffect(() => {
    if (phase) {
      setFormData({
        name: phase.name || '',
        typical_duration_days: phase.typical_duration_days || 1,
        description: phase.description || ''
      })
    } else {
      setFormData({
        name: '',
        typical_duration_days: 1,
        description: ''
      })
    }
  }, [phase, open])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.name.trim()) return

    setSaving(true)
    try {
      if (isEdit) {
        const { error } = await updatePhase(phase.id, formData)
        if (error) throw error
        toast.success('Phase updated')
      } else {
        const { error } = await createPhase({
          ...formData,
          process_id: processId,
          sequence_order: 999 // Will be reordered
        })
        if (error) throw error
        toast.success('Phase created')
      }
      onOpenChange(false)
      onSave()
    } catch (err) {
      toast.error(err.message || 'Failed to save phase')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Phase' : 'Add Phase'}</DialogTitle>
          <DialogDescription>
            {isEdit ? 'Update the phase details.' : 'Create a new phase for this process.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Block Incubation"
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="duration">Duration (days)</Label>
              <Input
                id="duration"
                type="number"
                min="1"
                value={formData.typical_duration_days}
                onChange={(e) => setFormData(prev => ({ ...prev, typical_duration_days: parseInt(e.target.value) || 1 }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of this phase..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !formData.name.trim()}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isEdit ? 'Save' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Event Dialog (Add/Edit)
 */
function EventDialog({ open, onOpenChange, event, phaseId, onSave }) {
  const [formData, setFormData] = useState({
    name: '',
    event_type: 'milestone',
    offset_days: 0,
    description: ''
  })
  const [saving, setSaving] = useState(false)
  const { createEvent, updateEvent } = useEventMutations()

  const isEdit = !!event

  useEffect(() => {
    if (event) {
      setFormData({
        name: event.name || '',
        event_type: event.event_type || 'milestone',
        offset_days: event.offset_days || 0,
        description: event.description || ''
      })
    } else {
      setFormData({
        name: '',
        event_type: 'milestone',
        offset_days: 0,
        description: ''
      })
    }
  }, [event, open])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.name.trim()) return

    setSaving(true)
    try {
      if (isEdit) {
        const { error } = await updateEvent(event.id, formData)
        if (error) throw error
        toast.success('Event updated')
      } else {
        const { error } = await createEvent({
          ...formData,
          phase_id: phaseId,
          sequence_order: 999
        })
        if (error) throw error
        toast.success('Event created')
      }
      onOpenChange(false)
      onSave()
    } catch (err) {
      toast.error(err.message || 'Failed to save event')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Event' : 'Add Event'}</DialogTitle>
          <DialogDescription>
            {isEdit ? 'Update the event details.' : 'Create a new event for this phase.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="event-name">Name</Label>
              <Input
                id="event-name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Check colonisation"
                autoFocus
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="event-type">Type</Label>
                <Select
                  value={formData.event_type}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, event_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="start">Start</SelectItem>
                    <SelectItem value="milestone">Milestone</SelectItem>
                    <SelectItem value="end">End</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="offset-days">Day offset</Label>
                <Input
                  id="offset-days"
                  type="number"
                  min="0"
                  value={formData.offset_days}
                  onChange={(e) => setFormData(prev => ({ ...prev, offset_days: parseInt(e.target.value) || 0 }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="event-description">Description (optional)</Label>
              <Textarea
                id="event-description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !formData.name.trim()}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isEdit ? 'Save' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Task Dialog (Add/Edit)
 */
function TaskDialog({ open, onOpenChange, task, eventId, onSave }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    estimated_minutes: '',
    is_required: false
  })
  const [saving, setSaving] = useState(false)
  const { createTask, updateTask } = useTaskMutations()

  const isEdit = !!task

  useEffect(() => {
    if (task) {
      setFormData({
        name: task.name || '',
        description: task.description || '',
        estimated_minutes: task.estimated_minutes || '',
        is_required: task.is_required || false
      })
    } else {
      setFormData({
        name: '',
        description: '',
        estimated_minutes: '',
        is_required: false
      })
    }
  }, [task, open])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.name.trim()) return

    setSaving(true)
    try {
      const payload = {
        name: formData.name,
        description: formData.description || null,
        estimated_minutes: formData.estimated_minutes ? parseInt(formData.estimated_minutes) : null,
        is_required: formData.is_required
      }

      if (isEdit) {
        const { error } = await updateTask(task.id, payload)
        if (error) throw error
        toast.success('Task updated')
      } else {
        const { error } = await createTask({
          ...payload,
          event_id: eventId,
          sequence_order: 999
        })
        if (error) throw error
        toast.success('Task created')
      }
      onOpenChange(false)
      onSave()
    } catch (err) {
      toast.error(err.message || 'Failed to save task')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Task' : 'Add Task'}</DialogTitle>
          <DialogDescription>
            {isEdit ? 'Update the task details.' : 'Create a new task for this event.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="task-name">Name</Label>
              <Input
                id="task-name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Inspect blocks"
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="task-description">Description (optional)</Label>
              <Textarea
                id="task-description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Instructions for this task..."
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="estimated-minutes">Est. minutes</Label>
                <Input
                  id="estimated-minutes"
                  type="number"
                  min="1"
                  value={formData.estimated_minutes}
                  onChange={(e) => setFormData(prev => ({ ...prev, estimated_minutes: e.target.value }))}
                  placeholder="15"
                />
              </div>
              <div className="space-y-2 flex items-end pb-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={formData.is_required}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_required: !!checked }))}
                  />
                  <span className="text-sm">Required</span>
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !formData.name.trim()}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isEdit ? 'Save' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Delete Confirmation Dialog
 */
function DeleteDialog({ open, onOpenChange, title, description, onConfirm, loading }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={loading}>
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Main component
 */
export default function ProcessEditorKanban({ user, onLogout }) {
  const { id } = useParams()
  const navigate = useNavigate()

  const { process, phases, loading, error, refetch } = useProcess(id)
  const { deletePhase, reorderPhases, updatePhase } = usePhaseMutations()
  const { deleteEvent } = useEventMutations()
  const { deleteTask } = useTaskMutations()

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Dialog states
  const [phaseDialog, setPhaseDialog] = useState({ open: false, phase: null })
  const [eventDialog, setEventDialog] = useState({ open: false, event: null, phaseId: null })
  const [taskDialog, setTaskDialog] = useState({ open: false, task: null, eventId: null })
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, item: null })
  const [recipeDialog, setRecipeDialog] = useState({ open: false, phase: null })
  const [deleting, setDeleting] = useState(false)

  // Handlers
  const handleAddPhase = () => setPhaseDialog({ open: true, phase: null })
  const handleEditPhase = (phase) => setPhaseDialog({ open: true, phase })
  const handleDeletePhase = (phase) => setDeleteDialog({
    open: true,
    type: 'phase',
    item: phase
  })

  const handleAddEvent = (phase) => setEventDialog({ open: true, event: null, phaseId: phase.id })
  const handleEditEvent = (event) => setEventDialog({ open: true, event, phaseId: event.phase_id })
  const handleDeleteEvent = (event) => setDeleteDialog({
    open: true,
    type: 'event',
    item: event
  })

  const handleAddTask = (event) => setTaskDialog({ open: true, task: null, eventId: event.id })
  const handleEditTask = (task) => setTaskDialog({ open: true, task, eventId: task.event_id })
  const handleDeleteTask = (task) => setDeleteDialog({
    open: true,
    type: 'task',
    item: task
  })

  const handleManageRecipe = (phase) => setRecipeDialog({ open: true, phase })

  const handleConfirmDelete = async () => {
    setDeleting(true)
    try {
      const { type, item } = deleteDialog
      let error

      if (type === 'phase') {
        ({ error } = await deletePhase(item.id))
      } else if (type === 'event') {
        ({ error } = await deleteEvent(item.id))
      } else if (type === 'task') {
        ({ error } = await deleteTask(item.id))
      }

      if (error) throw error
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} deleted`)
      setDeleteDialog({ open: false, type: null, item: null })
      refetch()
    } catch (err) {
      toast.error(err.message || 'Failed to delete')
    } finally {
      setDeleting(false)
    }
  }

  // Drag and drop handler
  const handleDragEnd = async (event) => {
    const { active, over } = event

    if (!over || active.id === over.id) {
      return
    }

    const oldIndex = sortedPhases.findIndex(p => p.id === active.id)
    const newIndex = sortedPhases.findIndex(p => p.id === over.id)

    if (oldIndex !== -1 && newIndex !== -1) {
      const newOrder = arrayMove(sortedPhases, oldIndex, newIndex)
      const orderedIds = newOrder.map(p => p.id)

      try {
        const { error } = await reorderPhases(orderedIds)
        if (error) throw error
        toast.success('Phases reordered')
        refetch()
      } catch (err) {
        toast.error('Failed to reorder phases')
      }
    }
  }

  const sortedPhases = [...phases].sort((a, b) => a.sequence_order - b.sequence_order)
  const totalDays = phases.reduce((sum, p) => sum + (p.typical_duration_days || 0), 0)
  const totalEvents = phases.reduce((sum, p) => sum + (p.phase_events?.length || 0), 0)
  const totalTasks = phases.reduce((sum, p) =>
    sum + (p.phase_events?.reduce((s, e) => s + (e.event_tasks?.length || 0), 0) || 0), 0)

  if (loading) {
    return (
      <PageLayout title="Process" user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center py-12">
          <div className="w-5 h-5 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin" />
        </div>
      </PageLayout>
    )
  }

  if (error || !process) {
    return (
      <PageLayout title="Process" user={user} onLogout={onLogout}>
        <div className="text-center py-12 text-muted-foreground">
          Process not found
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout
      title={process.name}
      subtitle={process.strains?.name ? `${process.strains.name} (${process.strains.code})` : 'Template'}
      user={user}
      onLogout={onLogout}
      headerActions={
        <Button variant="outline" onClick={() => navigate('/production/process')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
      }
    >
      <div className="h-full flex flex-col">
        {/* Summary bar */}
        <div className="flex items-center gap-6 px-4 py-3 border-b bg-muted/30 text-sm flex-shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Duration:</span>
            <span className="font-medium">{totalDays} days</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Phases:</span>
            <span className="font-medium">{phases.length}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Events:</span>
            <span className="font-medium">{totalEvents}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Tasks:</span>
            <span className="font-medium">{totalTasks}</span>
          </div>
        </div>

        {/* Kanban board */}
        <div className="flex-1 overflow-x-auto overflow-y-auto p-4">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sortedPhases.map(p => p.id)}
              strategy={horizontalListSortingStrategy}
            >
              <div className="flex gap-4 h-full">
                {sortedPhases.map((phase, index) => (
                  <PhaseColumn
                    key={phase.id}
                    phase={phase}
                    index={index}
                    onEditPhase={handleEditPhase}
                    onDeletePhase={handleDeletePhase}
                    onAddEvent={handleAddEvent}
                    onEditEvent={handleEditEvent}
                    onDeleteEvent={handleDeleteEvent}
                    onAddTask={handleAddTask}
                    onEditTask={handleEditTask}
                    onDeleteTask={handleDeleteTask}
                    onManageRecipe={handleManageRecipe}
                  />
                ))}

                {/* Add phase column */}
                <div className="w-72 flex-shrink-0">
                  <button
                    onClick={handleAddPhase}
                    className="w-full h-full min-h-[300px] rounded-lg border-2 border-dashed border-muted-foreground/20 text-muted-foreground hover:border-muted-foreground/40 hover:text-foreground transition-colors flex flex-col items-center justify-center gap-2"
                  >
                    <Plus className="h-5 w-5" />
                    <span className="text-sm">Add phase</span>
                  </button>
                </div>
              </div>
            </SortableContext>
          </DndContext>
        </div>
      </div>

      {/* Dialogs */}
      <PhaseDialog
        open={phaseDialog.open}
        onOpenChange={(open) => setPhaseDialog(prev => ({ ...prev, open }))}
        phase={phaseDialog.phase}
        processId={id}
        onSave={refetch}
      />

      <EventDialog
        open={eventDialog.open}
        onOpenChange={(open) => setEventDialog(prev => ({ ...prev, open }))}
        event={eventDialog.event}
        phaseId={eventDialog.phaseId}
        onSave={refetch}
      />

      <TaskDialog
        open={taskDialog.open}
        onOpenChange={(open) => setTaskDialog(prev => ({ ...prev, open }))}
        task={taskDialog.task}
        eventId={taskDialog.eventId}
        onSave={refetch}
      />

      <DeleteDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog(prev => ({ ...prev, open }))}
        title={`Delete ${deleteDialog.type}?`}
        description={
          deleteDialog.type === 'phase'
            ? 'This will delete all events and tasks within this phase. This action cannot be undone.'
            : deleteDialog.type === 'event'
            ? 'This will delete all tasks within this event. This action cannot be undone.'
            : 'This task will be permanently deleted.'
        }
        onConfirm={handleConfirmDelete}
        loading={deleting}
      />

      {/* Recipe Dialog */}
      <Dialog
        open={recipeDialog.open}
        onOpenChange={(open) => setRecipeDialog(prev => ({ ...prev, open }))}
      >
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Recipe for {recipeDialog.phase?.name}
            </DialogTitle>
            <DialogDescription>
              Define the ingredients and materials required for this phase.
            </DialogDescription>
          </DialogHeader>
          {recipeDialog.phase && (
            <RecipeTab
              recipeType={getRecipeType(recipeDialog.phase.name)}
              strainId={process?.strain_id}
              currentRecipeId={recipeDialog.phase.recipe_template_id}
              onRecipeChange={async (recipeTemplateId) => {
                // Update the phase with the new recipe
                const { error } = await updatePhase(recipeDialog.phase.id, {
                  recipe_template_id: recipeTemplateId
                })
                if (error) {
                  toast.error(`Failed to update recipe: ${error.message}`)
                } else {
                  toast.success('Recipe updated')
                  refetch()
                }
              }}
              readOnly={false}
            />
          )}
        </DialogContent>
      </Dialog>
    </PageLayout>
  )
}

/**
 * Helper to determine recipe type from phase name
 */
function getRecipeType(phaseName) {
  const name = (phaseName || '').toLowerCase()
  if (name.includes('spawn')) return 'spawn'
  if (name.includes('lc') || name.includes('liquid culture')) return 'liquid_culture'
  if (name.includes('block') || name.includes('substrate') || name.includes('inoculat')) return 'block'
  return 'block' // default
}
